import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowUpDown, Columns3, Eye, Store, UtensilsCrossed, AlertTriangle } from 'lucide-react';
import { adminService } from '../services/admin-service';
import { RUBROS, type ComparacionDeEmpresas, type EmpresaComparada } from '../types/admin.types';

/**
 * Las empresas lado a lado. Criterio de aceptación de IP-76.
 *
 * 🔴 Por qué esta pantalla no es "otro listado de empresas". El listado de
 * `/plataforma/empresas` sirve para **encontrar** una empresa: busca por nombre, pagina de
 * a 20, ordena por fecha de alta. Esta sirve para **comparar**: todas juntas, la
 * misma métrica en la misma unidad, ordenable por cualquiera de ellas, y con una
 * barra que hace visible la proporción sin tener que dividir mentalmente dos
 * números de cuatro cifras.
 *
 * El rubro (Retail / Comida) es la columna que agrupa, porque es la que dice contra
 * qué se compara cada empresa — y por lo tanto qué números tienen sentido de sumar
 * entre sí.
 */

/** Las columnas comparables. El `id` es también la llave del objeto en la fila. */
const COLUMNAS = [
  { id: 'usuarios', etiqueta: 'Usuarios', ayuda: 'Cuentas creadas en la empresa' },
  { id: 'cotizaciones', etiqueta: 'Cotizaciones', ayuda: 'Total histórico' },
  { id: 'cotizaciones30d', etiqueta: 'Últimos 30 d', ayuda: 'Cotizaciones del último mes' },
  { id: 'ordenes', etiqueta: 'Órdenes', ayuda: 'Órdenes de importación (Trade)' },
  { id: 'productos', etiqueta: 'Productos', ayuda: 'Productos con precio monitoreado' },
] as const;

type ColumnaId = (typeof COLUMNAS)[number]['id'];

const ICONO_RUBRO = { RETAIL: Store, COMIDA: UtensilsCrossed } as const;

/** Cuánto hace del último ingreso, en palabras. La fecha sola obliga a hacer la cuenta. */
function haceCuanto(iso: string | null): { texto: string; frio: boolean } {
  if (!iso) return { texto: 'nunca entró', frio: true };
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return { texto: 'hoy', frio: false };
  if (dias === 1) return { texto: 'ayer', frio: false };
  if (dias < 30) return { texto: `hace ${dias} días`, frio: false };
  if (dias < 60) return { texto: 'hace un mes', frio: true };
  return { texto: `hace ${Math.floor(dias / 30)} meses`, frio: true };
}

/**
 * Un número con la barra de su proporción contra el máximo de la columna.
 *
 * La barra es lo que convierte la tabla en una comparación: 18 al lado de 240 se
 * lee, pero doce columnas de números de cuatro cifras no. Se compara contra el
 * máximo de **esa** columna, no contra un total, porque las unidades no son
 * comparables entre columnas.
 */
function Celda({ valor, maximo }: { valor: number; maximo: number }) {
  const proporcion = maximo > 0 ? valor / maximo : 0;
  return (
    <div className="space-y-1">
      <div className="numero-grande text-lg">{valor.toLocaleString('es-BO')}</div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div
          className="h-full rounded-full bg-primary/70 transition-[width] duration-500"
          style={{ width: `${Math.max(proporcion * 100, valor > 0 ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
}

export function AdminComparacionPage() {
  const navigate = useNavigate();
  const [datos, setDatos] = useState<ComparacionDeEmpresas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incluirVacias, setIncluirVacias] = useState(false);
  const [orden, setOrden] = useState<ColumnaId | 'nombre'>('cotizaciones');

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setError(null);
    adminService
      .getComparacion(incluirVacias)
      .then((d) => {
        if (vigente) setDatos(d);
      })
      .catch((e) => {
        if (vigente) setError(e?.response?.data?.message ?? 'No se pudo cargar la comparación');
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [incluirVacias]);

  /*
    Los máximos por columna se calculan sobre TODAS las empresas mostradas, no por
    rubro: si cada grupo tuviera su propia escala, la barra más larga de Comida y la
    más larga de Retail se verían iguales midiendo cosas distintas.
  */
  const maximos = useMemo(() => {
    const m = {} as Record<ColumnaId, number>;
    for (const c of COLUMNAS) {
      m[c.id] = Math.max(0, ...(datos?.empresas ?? []).map((e) => e[c.id]));
    }
    return m;
  }, [datos]);

  const porRubro = useMemo(() => {
    const empresas = datos?.empresas ?? [];
    const ordenar = (a: EmpresaComparada, b: EmpresaComparada) =>
      orden === 'nombre' ? a.nombre.localeCompare(b.nombre) : b[orden] - a[orden];
    return (['RETAIL', 'COMIDA'] as const).map((rubro) => ({
      rubro,
      totales: datos?.porRubro.find((t) => t.rubro === rubro),
      empresas: empresas.filter((e) => e.rubro === rubro).sort(ordenar),
    }));
  }, [datos, orden]);

  return (
    <AppLayout variante="plataforma">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Columns3 className="h-6 w-6 text-primary" />
              Comparación de empresas
            </h1>
            <p className="text-sm text-muted-foreground">
              Las mismas métricas para todas, agrupadas por rubro.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <label className="etiqueta-caps flex items-center gap-2" htmlFor="ordenar-por">
              Ordenar por
            </label>
            {/*
              Un `select` nativo y no el del sistema de diseño: son seis opciones sin
              estado ni iconos, y acá el nativo da el teclado y el foco correctos sin
              nada más encima.
            */}
            <select
              id="ordenar-por"
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={orden}
              onChange={(e) => setOrden(e.target.value as ColumnaId | 'nombre')}
            >
              <option value="nombre">Nombre</option>
              {COLUMNAS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </div>
        </div>

        {error && (
          <Card className="border-destructive/40">
            <CardContent className="flex items-center gap-2 py-4 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </CardContent>
          </Card>
        )}

        {cargando && (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        )}

        {!cargando &&
          datos &&
          porRubro.map(({ rubro, totales, empresas }) => {
            const Icono = ICONO_RUBRO[rubro];
            return (
              <Card key={rubro}>
                <CardContent className="p-0">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
                    <div className="flex items-center gap-2">
                      <Icono className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                      <Badge variant="outline" className={RUBROS[rubro].clases}>
                        {RUBROS[rubro].etiqueta}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {empresas.length} {empresas.length === 1 ? 'empresa' : 'empresas'}
                      </span>
                    </div>
                    {/* Los totales del rubro: es lo que se compara entre rubros. */}
                    {totales && empresas.length > 0 && (
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                        {COLUMNAS.map((c) => (
                          <div key={c.id} className="flex items-baseline gap-1.5">
                            <span className="etiqueta-caps">{c.etiqueta}</span>
                            <span className="numero-grande text-base">
                              {totales[c.id].toLocaleString('es-BO')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {empresas.length === 0 ? (
                    <p className="p-6 text-sm text-muted-foreground">
                      Todavía no hay ninguna empresa de este rubro.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Empresa</TableHead>
                            <TableHead>Plan</TableHead>
                            <TableHead>Último ingreso</TableHead>
                            {COLUMNAS.map((c) => (
                              <TableHead key={c.id} title={c.ayuda}>
                                {c.etiqueta}
                              </TableHead>
                            ))}
                            <TableHead className="w-10" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {empresas.map((e) => {
                            const visto = haceCuanto(e.ultimoIngreso);
                            return (
                              <TableRow key={e.id}>
                                <TableCell>
                                  <div className="font-medium">{e.nombre}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {e.usuariosActivos} de {e.usuarios} usuarios activos
                                    {!e.activa && ' · empresa desactivada'}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {e.plan ? (
                                    <div className="text-sm">
                                      {e.plan.nombre}
                                      {e.plan.prueba && (
                                        <Badge variant="secondary" className="ml-1.5">
                                          prueba
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">sin plan</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={
                                      visto.frio ? 'text-sm text-muted-foreground' : 'text-sm'
                                    }
                                  >
                                    {visto.texto}
                                  </span>
                                </TableCell>
                                {COLUMNAS.map((c) => (
                                  <TableCell key={c.id}>
                                    <Celda valor={e[c.id]} maximo={maximos[c.id]} />
                                  </TableCell>
                                ))}
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Ver ${e.nombre}`}
                                    onClick={() => navigate(`/plataforma/empresas/${e.id}`)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

        {/*
          Se dice qué quedó afuera y por qué. Un total de 142 arriba y 41 filas
          abajo, sin explicación, se lee como un error del sistema.
        */}
        {!cargando && datos && datos.ocultas > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>
              No se muestran {datos.ocultas} de {datos.totalEmpresas} empresas: nadie entró nunca a
              ellas.
            </span>
            <Button variant="link" className="h-auto p-0" onClick={() => setIncluirVacias(true)}>
              Mostrarlas igual
            </Button>
          </div>
        )}
        {!cargando && datos && incluirVacias && (
          <Button variant="link" className="h-auto p-0" onClick={() => setIncluirVacias(false)}>
            Ocultar las empresas sin actividad
          </Button>
        )}
      </div>
    </AppLayout>
  );
}
